import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { ClientEvent, SyncState, EventType, RoomStateEvent, RoomEvent, NotificationCountType, MatrixEventEvent } from '../constants/matrixEvents';
import { useMatrixClient } from '../context/MatrixContext';
import { useI18n } from '../context/I18nContext';
import { useAvatar } from '../context/AvatarContext';
import { getLastActivityTimestamp, getLastConversationEvent } from '../utils/matrixEvents';
import { GEMATIK_ROOM } from '../constants';
import matrixService from '../services/matrixService';
import logger from '../utils/logger';
import { mergeAvatarDescriptors } from '../utils/mergeAvatarDescriptors';
import { getRoomDisplayName, getRoomTopic } from '../utils/gematikRoomUtils';
import { isServerNoticesRoom } from '../utils/serverNoticesUtils';
import { getRoomPushRuleState, PushRuleState } from '../utils/pushRuleState';
import { resolveUnreadCount, isReadMarkerInTimeline, getEffectiveReadMarkerEventId } from '../utils/unreadCountUtils';
import { ensureReadMarkerInTimeline, runWithLimit } from '../utils/unreadBackfill';
import { clearFloorForRoom, wireCrossTabSync, maybeInvalidateFloorForReceipt, extractOwnReceiptTs } from '../utils/unreadFloor';
import { useIgnoredUsers } from './useIgnoredUsers';
import { useDisambiguation } from '../context/DisambiguationContext';
import { getDMDisambiguatedName } from '../utils/disambiguationUtils';

/**
 * Determines if a timeline event should trigger room list re-ordering (UI update).
 *
 * IMPORTANT: This function is intentionally MORE RESTRICTIVE than isConversationActivityEvent().
 * While both functions classify events, they serve different purposes:
 *
 * - isConversationActivityEvent(): Determines what counts as "activity" for TIMESTAMP calculation
 *   (broader scope - includes metadata changes like m.room.name, m.room.avatar, m.room.topic)
 *
 * - shouldCauseReorder(): Determines what should trigger ROOM LIST REORDERING (UI update)
 *   (narrower scope - excludes metadata to prevent UI thrashing)
 *
 * DESIGN RATIONALE:
 *
 * Metadata events (m.room.name, m.room.avatar, m.room.topic) ARE conversation activity
 * and DO update the lastActivity timestamp, but they should NOT trigger room reordering
 * because:
 *
 * 1. PERFORMANCE: Prevents UI thrashing during batch metadata updates
 *    Example: Admin setting up a new channel
 *      - Sets room name → Room jumps to top
 *      - Sets topic → Room jumps to top again
 *      - Uploads avatar → Room jumps to top again
 *      - Tweaks name → Room jumps to top again
 *    This creates visual chaos and poor UX.
 *
 * 2. USER EXPECTATIONS: Metadata changes are administrative, not conversational
 *    Example: Admin renames "#project-alpha" → "#project-beta"
 *      - This shouldn't compete with active message conversations
 *      - Users expect rooms to bubble up based on messages, not config changes
 *
 * 3. FREQUENCY: Metadata changes are rare (~1 per week) vs messages (~10-500 per hour)
 *    They don't represent active conversation flow
 *
 * HISTORICAL NOTE (2025-10-20):
 *
 * A proposal was made to align this function with isConversationActivityEvent() by
 * including metadata events. After thorough investigation using devil's advocate analysis:
 *
 * - NO evidence found of metadata exclusion causing problems
 * - Tests explicitly verify metadata should NOT trigger reorder (8+ tests)
 * - Code review showed this is intentional design, not a bug
 * - Decision: Keep current implementation, add comprehensive documentation
 *
 * See: .claude/tasks/test-failures/MILLISECOND_CONVERSION_REPORT.md
 *
 * EXAMPLES:
 *
 * Events that SHOULD trigger reorder:
 *   ✅ m.room.message - User sends a message
 *   ✅ m.room.encrypted - User sends encrypted message
 *   ✅ m.reaction - User reacts to a message
 *   ✅ m.call.invite - Incoming call
 *   ✅ m.room.member (membership changes only) - User joins/leaves
 *
 * Events that should NOT trigger reorder:
 *   ❌ m.room.name - Admin changes room name
 *   ❌ m.room.avatar - Admin uploads new room avatar
 *   ❌ m.room.topic - Admin updates room topic
 *   ❌ m.typing - User is typing (ephemeral)
 *   ❌ m.receipt - Read receipt (ephemeral)
 *   ❌ m.room.member (profile changes) - User changes displayname/avatar
 *
 * RELATED FUNCTIONS:
 * - isConversationActivityEvent() in src/utils/matrixEvents.js - Timestamp calculation
 * - getLastActivityTimestamp() in src/utils/matrixEvents.js - Uses isConversationActivityEvent()
 * - handleRoomAvatarChange() in this file - Updates avatars in-place without reordering
 *
 * @param {object} event - Matrix event object
 * @returns {boolean} - True if event should cause room list reordering, false otherwise
 */
export const shouldCauseReorder = (event) => {
  if (!event) return false;

  const eventType = event.getType();

  // Conversation events that should trigger re-ordering
  const conversationEvents = [
    'm.room.message',      // Regular messages
    'm.room.encrypted',    // Encrypted messages
    'm.sticker',           // Stickers
    'm.reaction',          // Message reactions
    'm.room.redaction',    // Message deletions
    'm.call.invite',       // Incoming call
    'm.call.answer',       // Call answered
    'm.call.hangup',       // Call ended
    'm.room.pinned_events', // Pin/unpin messages
    // Note: m.room.member is handled separately below
  ];

  // Events that should NOT trigger re-ordering
  const ignoredEvents = [
    'm.typing',            // Typing indicators
    'm.receipt',           // Read receipts
    'm.presence',          // Presence updates
    'm.room.avatar',       // Room avatar changes
    'm.room.name',         // Room name changes (metadata only)
    GEMATIK_ROOM.ROOM_NAME, // Gematik room name changes (metadata only)
    'm.room.power_levels', // Power level changes
    'm.room.topic',        // Room topic changes
    GEMATIK_ROOM.ROOM_TOPIC, // Gematik room topic changes
    'm.room.join_rules',   // Join rules changes
    'm.room.history_visibility', // History visibility changes
  ];

  // Fast path: explicitly ignored events
  if (ignoredEvents.includes(eventType)) {
    return false;
  }

  // Handle other Gematik events - for now, treat them as non-reordering metadata
  if (eventType.startsWith('de.gematik.tim.')) {
    // Most Gematik events are likely metadata/configuration
    // Add specific ones to conversationEvents array if they should cause reordering
    return false;
  }
  // Member events: Only membership changes should trigger reordering
  // Profile changes (avatar, displayname) are metadata-only and should NOT reorder
  if (eventType === 'm.room.member') {
    const content = event.getContent();
    const prevContent = event.getPrevContent();

    // If membership changed (join/leave/ban/invite), allow update
    if (content.membership !== prevContent?.membership) {
      return true;
    }

    // Profile changes (avatar, displayname) should NOT trigger conversation reordering
    // This applies to ALL users (self and others) changing their profile
    // Avatar/displayname updates are handled separately by handleMemberStateChange
    // which updates the UI without affecting conversation sort order
    return false;
  }

  // Allow conversation events
  return conversationEvents.includes(eventType);
};

/**
 * Helper to check if an event type is ephemeral (should not appear in conversation list)
 */
const isEphemeralEvent = (eventType) => {
  return ['m.typing', 'm.receipt', 'm.presence'].includes(eventType);
};

/**
 * Helper to check if an event is metadata-only (should not appear in conversation list)
 */
const isMetadataOnlyEvent = (eventType) => {
  return [
    'm.room.avatar',
    'm.room.name',
    GEMATIK_ROOM.ROOM_NAME,
    'm.room.power_levels',
    'm.room.topic',
    GEMATIK_ROOM.ROOM_TOPIC,
    'm.room.join_rules',
    'm.room.history_visibility',
    'm.room.encryption',
  ].includes(eventType);
};

/**
 * Wrapper for the shared getLastConversationEvent function.
 * Maintains the internal API while using the consolidated logic from matrixEvents.js
 *
 * @param {Room} room - Matrix room object
 * @param {string} myUserId - Current user's Matrix ID
 * @param {function} t - Translation function
 * @returns {Object|null} Event object with type, body, senderName, timestamp, etc.
 */
const getLastEvent = (room, myUserId, t, ignoredUsers) => {
  return getLastConversationEvent(room, myUserId, t, ignoredUsers);
};

const getLastActivity = (room, myUserId) => {
  const membership = room.getMyMembership();

  // Defensive check for room initialization phase
  if (!room?.currentState) {
    return 0;
  }

  // For invited rooms, use the invite event timestamp so they appear at the top
  if (membership === 'invite' && myUserId) {
    const memberEvent = room.currentState.getStateEvents('m.room.member', myUserId);
    if (memberEvent) {
      const inviteTs = memberEvent.getTs();
      // Matrix event timestamps are in milliseconds - return as-is
      // Type check to prevent NaN from invalid values
      if (inviteTs && typeof inviteTs === 'number' && !Number.isNaN(inviteTs)) {
        return Math.floor(inviteTs);
      }
    }
  }

  const timeline = room.getLiveTimeline();
  if (!timeline) {
    // No timeline - use stable room creation timestamp
    const createEvent = room.currentState.getStateEvents('m.room.create', '');
    if (createEvent) {
      const createTs = createEvent.getTs();
      // Matrix event timestamps are in milliseconds - return as-is
      // Type check to prevent NaN from invalid values
      if (createTs && typeof createTs === 'number' && !Number.isNaN(createTs)) {
        return Math.floor(createTs);
      }
    }
    return 0;
  }
  
  const events = timeline.getEvents();

  // Filter out profile-only changes from m.room.member events
  // This prevents rooms from reordering when users change their avatar or displayname
  // while still allowing the preview text to update
  const filteredEvents = events.filter(event => {
    const type = event.getType();
    if (type === 'm.room.member') {
      const content = event.getContent();
      const prevContent = event.getPrevContent();

      // If membership changed, include the event (join/leave/ban/invite)
      if (content.membership !== prevContent?.membership) {
        return true;
      }

      // If only profile changed (avatar or displayname), exclude from timestamp calculation
      const avatarChanged = content.avatar_url !== prevContent?.avatar_url;
      const displaynameChanged = content.displayname !== prevContent?.displayname;
      if (avatarChanged || displaynameChanged) {
        return false; // Exclude profile-only changes
      }
    }
    return true; // Include all other events
  });

  // Get timestamp of most recent conversation activity using shared utility
  // This filters out ephemeral events (typing, receipts, presence) and
  // state-only configuration events (power_levels, encryption, etc.)
  const activityTimestamp = getLastActivityTimestamp(filteredEvents);
  if (activityTimestamp) {
    // getLastActivityTimestamp returns MILLISECONDS - return as-is
    // lastActivity field stores Unix timestamps in milliseconds (matching Element Web)
    return activityTimestamp;
  }

  // No conversation events found - use STABLE fallback instead of getLastActiveTimestamp()
  // which can change on every sync. Use room creation time which is immutable.
  const createEvent = room.currentState.getStateEvents('m.room.create', '');
  if (createEvent) {
    const createTs = createEvent.getTs();
    // Matrix event timestamps are in milliseconds - return as-is
    return createTs ? Math.floor(createTs) : 0;
  }
  
  // Last resort - use getLastActiveTimestamp (unstable but better than 0)
  const fallbackTs = room.getLastActiveTimestamp();
  // getLastActiveTimestamp returns milliseconds - return as-is
  return fallbackTs ? Math.floor(fallbackTs) : 0;
};

/**
 * A hook to get a list of rooms formatted for display in a list.
 * It provides the room's name, its last message, and activity timestamp.
 * @returns {{roomList: object[], isLoading: boolean}}
 */
export const useRoomList = () => {
  const client = useMatrixClient();
  const { t } = useI18n();
  const [rooms, setRooms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [roomAvatars, setRoomAvatars] = useState({});
  const [initialSyncComplete, setInitialSyncComplete] = useState(false);
  const [updateTrigger, setUpdateTrigger] = useState(0);
  const [pushRulesVersion, setPushRulesVersion] = useState(0);
  // Bumps when m.fully_read account data arrives for any room, so the
  // backfill effect retries rooms whose marker was null on first pass (NED-3439).
  const [accountDataVersion, setAccountDataVersion] = useState(0);
  const ignoredUsers = useIgnoredUsers(client);
  const { needsDisambiguation } = useDisambiguation();

  // Debounce timer for performance optimization
  const debounceTimerRef = useRef(null);

  // Ref to track current rooms for event handlers (avoids stale closures)
  const roomsRef = useRef(rooms);
  useEffect(() => {
    roomsRef.current = rooms;
  }, [rooms]);


  /**
   * Debounced update to prevent excessive re-renders from rapid Matrix events.
   * Uses 150ms delay for UI responsiveness.
   */
  const debouncedUpdateRooms = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setUpdateTrigger(prev => prev + 1);
      debounceTimerRef.current = null;
    }, 150);
  }, []);

  // Create stable room identifier for avatar effect dependency
  // Only changes when rooms are added/removed, not on every sync
  const roomIds = useMemo(() => {
    return rooms.map(r => r.roomId).sort().join(',');
  }, [rooms]);

  const addRoom = useCallback(async (roomName, inviteUsers = [], initialMessage = undefined) => {
    if (!client) {
      logger.error('Matrix client not available to create room');
      throw new Error('Matrix client is not available to create a room.');
    }

    // Only treat as DM if exactly one invitee (prevents multi-user edge case)
    // DMs MUST be 1-on-1 to properly update m.direct account data
    const isDirectMessage = inviteUsers.length === 1;

    // Check for existing DM to prevent duplicates (like Element Web does)
    if (isDirectMessage) {
      const existingDM = matrixService.findDMForUser(inviteUsers[0]);
      if (existingDM) {
        logger.info(`DM already exists with ${inviteUsers[0]}, returning existing room: ${existingDM.roomId}`);
        // If there's an initial message, send it to the existing room
        if (initialMessage && typeof initialMessage === 'string' && initialMessage.trim()) {
          const content = {
            body: initialMessage.trim(),
            msgtype: 'm.text',
          };
          await client.sendMessage(existingDM.roomId, content);
        }
        return existingDM.roomId;
      }
    }

    const roomOptions = {
      invite: inviteUsers,
      // Use trusted_private_chat preset for DMs as per Matrix spec
      preset: isDirectMessage ? 'trusted_private_chat' : 'private_chat',
      creation_content: {
        type: GEMATIK_ROOM.ROOM_TYPE,
      },
      initial_state: [
        {
          type: EventType.RoomEncryption,
          state_key: '',
          content: { algorithm: 'm.megolm.v1.aes-sha2' },
        },
        {
          type: GEMATIK_ROOM.ROOM_DEFAULT,
          content: {},
          state_key: '',
        },
        {
          type: 'm.room.history_visibility',
          state_key: '',
          content: { history_visibility: 'invited' },
        },
      ],
    };

    // Add the room name as separate state events if provided.
    // Send both Gematik custom event AND standard Matrix event for compatibility.
    // Timeline display filters to show only standard Matrix events (avoids duplicates).
    // See: gemSpec_TI-M_Basis_V1.1.2 section 5.5.1.1
    if (roomName) {
      roomOptions.initial_state.push({
        type: GEMATIK_ROOM.ROOM_NAME,
        content: {
          name: roomName,
        },
        state_key: '',
      });
      roomOptions.initial_state.push({
        type: 'm.room.name',
        content: {
          name: roomName,
        },
        state_key: '',
      });
    }

    // Set is_direct flag to signal invitees that this is a DM
    // NOTE: This flag is for compatibility with other Matrix clients (Element, etc.)
    // IMPORTANT: The is_direct flag alone does NOT persist DM status in this implementation
    // The ONLY source of truth for DM status is m.direct account data (set via setDMRoom below)
    if (isDirectMessage) {
      roomOptions.is_direct = true;
    }

    try {
      const { room_id: roomId } = await client.createRoom(roomOptions);

      // CRITICAL: Manually update m.direct account data for DM rooms
      // The is_direct flag alone does NOT persist DM status - we must explicitly
      // update the m.direct account data ourselves. This is how Element Web does it.
      if (isDirectMessage) {
        try {
          await matrixService.setDMRoom(roomId, inviteUsers[0]);
        } catch (error) {
          logger.error('Failed to update m.direct account data:', error);
          // Don't fail the room creation, but log the error
        }
      }

      // Wait for the next sync to complete. This ensures that when we navigate
      // to the new room, the client has processed its state events (like the name)
      // and it will appear correctly in the room list.
      // Timeout fallback: if sync doesn't fire within 10s (e.g. race condition where
      // the sync event fired between createRoom resolving and the listener attaching),
      // proceed anyway — the room will appear on the next sync cycle.
      await new Promise(resolve => {
        const timeout = setTimeout(() => {
          client.off(ClientEvent.Sync, onSync);
          resolve();
        }, 10000);

        const onSync = (state) => {
          if (state === SyncState.Syncing) {
            clearTimeout(timeout);
            client.off(ClientEvent.Sync, onSync);
            resolve();
          }
        };

        client.on(ClientEvent.Sync, onSync);
      });

      // If an initial message is provided, send it immediately after creation.
      if (initialMessage && typeof initialMessage === 'string' && initialMessage.trim()) {
        const content = {
          body: initialMessage.trim(),
          msgtype: 'm.text',
        };

        await client.sendMessage(roomId, content);
      }

      return roomId;
    } catch (error) {
      logger.error('Failed to create room:', error);
      throw error;
    }
  }, [client]);

  /**
   * Handle unread notification count changes
   * This must be defined outside the main useEffect so it can be used by the room-level listener effect
   */
  const handleUnreadNotifications = useCallback((room) => {
    if (!room) return;

    // Check if room is tracked before triggering update (using ref to avoid stale closures)
    const isTracked = roomsRef.current.some(r => r.roomId === room.roomId);
    if (!isTracked) return;

    logger.debug('[NED-3388] RoomEvent.UnreadNotifications fired', {
      roomId: room.roomId,
      sdkCount: room.getUnreadNotificationCount?.(NotificationCountType.Total),
    });

    // Trigger debounced update to refresh unread badges
    debouncedUpdateRooms();
  }, [debouncedUpdateRooms]);

  useEffect(() => {
    if (!client) {
      setIsLoading(true);
      setRooms([]);
      return;
    }

    const updateRooms = () => {
      // Filter rooms to include only direct messages (rooms in m.direct account data)
      // Member count is irrelevant - m.direct is the sole source of truth
      const allRooms = client.getRooms();

      const newRooms = allRooms.filter(room => {
        // Filter out null/undefined rooms
        if (!room) return false;

        const membership = room.getMyMembership();
        const isDM = matrixService.isDMRoom(room.roomId);

        // Include both joined and invited rooms
        if (membership !== 'join' && membership !== 'invite') {
          return false;
        }

        // Only include rooms marked as direct in m.direct account data
        // This includes 1-on-1 DMs and group DMs (if user added members to a DM)
        return isDM;
      });

      // Use functional setState to compare with previous rooms
      // Only update state if the room IDs have actually changed
      // This prevents unnecessary memo recalculations when the same rooms
      // are returned with different object references
      setRooms(prevRooms => {
        const prevIds = prevRooms.map(r => r.roomId).sort().join(',');
        const newIds = newRooms.map(r => r.roomId).sort().join(',');

        if (prevIds === newIds) {
          // Same set of rooms - return previous reference to prevent memo recalc
          return prevRooms;
        }

        // Different rooms - update state
        return newRooms;
      });
    };

    const handleSync = (state) => {
      // BUG FIX: Also update rooms on Syncing state to catch real-time invites
      // updateRooms() has built-in optimization (compares room IDs) to prevent
      // unnecessary re-renders, so calling it on every sync is safe
      if (state === SyncState.Prepared || state === SyncState.Syncing) {
        updateRooms();
        if (state === SyncState.Prepared) {
          setIsLoading(false);
        }
        
        // Mark initial sync as complete when we reach Syncing state
        // This enables avatar invalidation for LIVE changes only
        if (state === SyncState.Syncing && !initialSyncComplete) {
          setInitialSyncComplete(true);
        }
      }
    };

    /**
     * Handle timeline events to determine if room list should be updated.
     * This is more efficient than re-fetching on every sync.
     * Similar to Element Web's approach.
     */
    const handleTimelineEvent = (event, room, toStartOfTimeline) => {
      // Ignore backfill events (these are old messages being loaded)
      if (toStartOfTimeline) return;

      // Special handling for membership changes to 'leave' - must immediately remove room
      // This fixes the bug where left rooms don't disappear until tab switch
      const eventType = event.getType();

      if (eventType === 'm.room.member') {
        const content = event.getContent();
        const prevContent = event.getPrevContent();
        const myUserId = client?.getUserId();
        const targetUserId = event.getStateKey();

        // If this is MY membership changing to 'leave', immediately remove room from list
        if (targetUserId === myUserId &&
            content.membership === 'leave' &&
            prevContent?.membership !== 'leave') {
          // Immediately remove the left room from state
          setRooms(prevRooms => prevRooms.filter(r => r.roomId !== room.roomId));
          return;
        }

        // If this is MY membership changing to 'ban', immediately remove room from list
        if (targetUserId === myUserId &&
            content.membership === 'ban' &&
            prevContent?.membership !== 'ban') {
          // Immediately remove the banned room from state
          setRooms(prevRooms => prevRooms.filter(r => r.roomId !== room.roomId));
          return;
        }

      }

      // Check if this event should trigger conversation reordering
      // Skip profile-only changes (avatar, displayname) to prevent unnecessary reordering
      if (!shouldCauseReorder(event)) {
        return;
      }

      // Check if this room should be in our filtered list
      const membership = room.getMyMembership();
      if (membership !== 'join' && membership !== 'invite') return;

      // Filter must match updateRooms() logic - only include DMs (rooms in m.direct)
      if (!matrixService.isDMRoom(room.roomId)) return;

      // Trigger debounced update to recalculate roomList memo
      // The memo will naturally filter to only tracked rooms
      debouncedUpdateRooms();
    };

    /**
     * Receipts move the local read marker before SDK notification counts always catch up (NED-3388).
     * RoomEvent.UnreadNotifications may not fire for every receipt path; without this the
     * conversation list memo never recomputes and badges stay stale until some other event.
     */
    const handleReceiptEvent = (event, room) => {
      if (!room) return;

      const membership = room.getMyMembership();
      if (membership !== 'join' && membership !== 'invite') return;

      if (!matrixService.isDMRoom(room.roomId)) return;

      // Cross-device read signal (NED-3439): if this Receipt event carries
      // our own read position with a ts strictly NEWER than the floor's
      // `ts`, the marker was advanced by another device while we were
      // offline / not viewing — invalidate the stale floor. Synth receipts
      // replayed during the initial sync carry their ORIGINAL (pre-refresh)
      // server ts, which is always older than the most recent local floor
      // write, so they correctly do not trip this check.
      const uid = client.getUserId();
      if (uid) {
        const maxOwnTs = extractOwnReceiptTs(event, uid);
        if (maxOwnTs > 0) {
          maybeInvalidateFloorForReceipt(uid, room.roomId, maxOwnTs);
        }
      }

      const isTracked = roomsRef.current.some(r => r.roomId === room.roomId);
      if (!isTracked) return;

      logger.debug('[useRoomList] Receipt event for DM, refreshing unread badges', { roomId: room.roomId });
      debouncedUpdateRooms();
    };

    /**
     * Handle account data changes that affect room visibility or grouping.
     * This is critical because ClientEvent.Sync does NOT fire for every sync response
     * once the client is in Syncing state. When account data changes, we receive a
     * ClientEvent.AccountData event immediately, allowing us to update the UI
     * without waiting for state transitions.
     *
     * Handles:
     * - m.direct: Room grouping changes (DM vs channel classification)
     * - m.ignored_user_list: When users are ignored/unignored, rooms may become visible/hidden
     * - m.push_rules: Mute state changes
     */
    const handleAccountData = (event) => {
      const eventType = event.getType();
      if (eventType === 'm.direct' || eventType === 'm.ignored_user_list') {
        updateRooms();
      }
      // Trigger re-render when push rules change to update mute states
      if (eventType === 'm.push_rules') {
        setPushRulesVersion(v => v + 1);
      }
    };

    /**
     * Handle event decryption to update room list when encrypted messages are decrypted.
     * Without this listener, encrypted messages show <encrypted> placeholder even after
     * successful decryption. This ensures the conversation list preview updates to show
     * the actual decrypted message content once decryption completes.
     *
     * OPTIMIZATION: Only processes decryption events for DM rooms we're actually tracking.
     * This prevents unnecessary re-renders when encrypted messages in channels are decrypted.
     */
    const handleEventDecrypted = (event) => {
      const roomId = event.getRoomId();
      if (!roomId) return;

      // Get room object to check filters
      const room = client?.getRoom(roomId);
      if (!room) return;

      // Apply same filtering logic as updateRooms() and handleTimelineEvent()
      const membership = room.getMyMembership();
      if (membership !== 'join' && membership !== 'invite') return;

      // Only update if this is a DM room we're tracking
      if (!matrixService.isDMRoom(roomId)) return;

      // Trigger immediate update to recalculate roomList memo (no debouncing for decryption)
      // The memo will naturally filter to only tracked rooms
      setUpdateTrigger(prev => prev + 1);
    };

    const syncState = client.getSyncState();

    if (syncState === SyncState.Prepared || syncState === SyncState.Syncing) {
      setIsLoading(false);
      updateRooms();

      // If already syncing when component mounts, mark initial sync as complete
      if (syncState === SyncState.Syncing) {
        setInitialSyncComplete(true);
      }
    } else {
      setIsLoading(true);
    }

    // Room-scoped m.fully_read updates arrive as RoomEvent.AccountData, re-emitted
    // through the client. Bump accountDataVersion so the backfill effect retries
    // rooms whose marker was null on first pass (NED-3439).
    const handleRoomAccountData = (event, room) => {
      if (event?.getType?.() === 'm.fully_read') {
        // Account-data events have no origin_server_ts in the Matrix spec, so
        // event.getTs() returns undefined. We must NOT fall back to Date.now()
        // — that would wrongly invalidate the floor on every cold-start replay
        // of our own pre-refresh marker, defeating NED-3439. Cross-device
        // marker advance is signalled via RoomEvent.Receipt (which carries a
        // real content-level ts), not via m.fully_read. Skip invalidation here
        // when ts is not a finite number.
        const uid = client.getUserId();
        const ts = typeof event.getTs === 'function' ? event.getTs() : undefined;
        if (uid && room?.roomId && Number.isFinite(ts)) {
          maybeInvalidateFloorForReceipt(uid, room.roomId, ts);
        }
        setAccountDataVersion(v => v + 1);
      }
    };

    // Purge the persisted floor when the user leaves a room, so the key
    // doesn't linger in localStorage forever (NED-3439 plan M3).
    const handleMyMembership = (room, membership) => {
      if (membership !== 'leave') return;
      const uid = client.getUserId();
      if (uid && room?.roomId) clearFloorForRoom(uid, room.roomId);
    };

    // Keep the in-memory floor cache in sync with other tabs (NED-3439 C2).
    const teardownCrossTabSync = wireCrossTabSync(client.getUserId());

    client.on(ClientEvent.Sync, handleSync);
    client.on(ClientEvent.Room, updateRooms);
    client.on(RoomEvent.Timeline, handleTimelineEvent); // For timeline events
    client.on(RoomEvent.Receipt, handleReceiptEvent); // Read markers → badge refresh (NED-3388)
    client.on(ClientEvent.AccountData, handleAccountData); // For m.direct changes
    client.on(RoomEvent.AccountData, handleRoomAccountData); // For m.fully_read (NED-3439)
    client.on(RoomEvent.MyMembership, handleMyMembership); // Purge floor on leave (NED-3439)
    client.on(MatrixEventEvent.Decrypted, handleEventDecrypted); // For encrypted message decryption

    return () => {
      // Cleanup debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      // REMOVED: Blob URL cleanup - avatarManager now handles all blob URL lifecycle
      // Direct revocation here caused race conditions where URLs were revoked while still in use
      // for (const blobUrl of blobUrlsRef.current.values()) {
      //   if (blobUrl && blobUrl.startsWith('blob:')) {
      //     URL.revokeObjectURL(blobUrl);
      //   }
      // }
      // blobUrlsRef.current.clear();

      teardownCrossTabSync();
      client.off(ClientEvent.Sync, handleSync);
      client.off(ClientEvent.Room, updateRooms);
      client.off(RoomEvent.Timeline, handleTimelineEvent);
      client.off(RoomEvent.Receipt, handleReceiptEvent);
      client.off(ClientEvent.AccountData, handleAccountData);
      client.off(RoomEvent.AccountData, handleRoomAccountData);
      client.off(RoomEvent.MyMembership, handleMyMembership);
      client.off(MatrixEventEvent.Decrypted, handleEventDecrypted);
    };
  }, [client]);

  /**
   * Backfill the live timeline of tracked rooms whose read marker has been
   * paginated out, so `resolveUnreadCount` can compute a trusted marker-based
   * count instead of falling back to the decryption-driven SDK count crawl
   * (NED-3439 cold-start refresh).
   *
   * Re-runs when the room set changes or when m.fully_read account data
   * arrives late (accountDataVersion bump).
   */
  useEffect(() => {
    if (!client || !rooms || rooms.length === 0) return undefined;
    const myUserId = client.getUserId();
    if (!myUserId) return undefined;

    let cancelled = false;

    const candidates = rooms.filter((room) => {
      try {
        const marker = getEffectiveReadMarkerEventId(room, myUserId);
        if (!marker) return false;
        return !isReadMarkerInTimeline(room, myUserId);
      } catch {
        return false;
      }
    });

    if (candidates.length === 0) return undefined;

    const tasks = candidates.map((room) => async () => {
      const marker = getEffectiveReadMarkerEventId(room, myUserId);
      if (!marker) return;
      const result = await ensureReadMarkerInTimeline(client, room, marker);
      if (cancelled) return;
      // Skip when the outcome kind is unchanged — parallel gate for the
      // useChannels loop fix (NED-3439 Workstream B).
      if ((result.found || result.cappedOut) && !result.alreadyEmitted) {
        setUpdateTrigger(prev => prev + 1);
      }
    });

    runWithLimit(tasks, 4).catch((err) => {
      logger.warn('[useRoomList] backfill runner error', { error: err?.message || String(err) });
    });

    return () => { cancelled = true; };
  }, [client, rooms, accountDataVersion]);

  // Register RoomEvent.UnreadNotifications on each room object
  // This event is emitted BY THE ROOM, not the client, so we must listen on each room individually
  useEffect(() => {
    if (!rooms || rooms.length === 0) return;

    const handlers = new Map();

    // Register listener on each room
    rooms.forEach(room => {
      const handler = () => handleUnreadNotifications(room);
      handlers.set(room.roomId, handler);
      room.on(RoomEvent.UnreadNotifications, handler);
    });

    // Cleanup: remove listeners from all rooms
    return () => {
      rooms.forEach(room => {
        const handler = handlers.get(room.roomId);
        if (handler) {
          room.off(RoomEvent.UnreadNotifications, handler);
        }
      });
    };
  }, [rooms, handleUnreadNotifications]); // handleUnreadNotifications is stable now

  // Listen for timeline refresh completion (e.g., after unignoring a user)
  // to update conversation list previews with newly fetched messages
  useEffect(() => {
    const handleTimelineRefreshed = () => {
      setUpdateTrigger(prev => prev + 1);
    };

    window.addEventListener('matrix:timeline:refreshed', handleTimelineRefreshed);
    return () => {
      window.removeEventListener('matrix:timeline:refreshed', handleTimelineRefreshed);
    };
  }, []);

  // Listen for room name changes to refresh conversation list display names (NED-2711)
  // shouldCauseReorder() intentionally ignores name events to prevent list reordering,
  // but we still need the memo to recalculate so display names update.
  // RoomEvent.Name fires when the SDK's calculated room name changes, covering both
  // m.room.name and display name recalculations. The Gematik custom event
  // (de.gematik.tim.room.name) is always dual-written alongside m.room.name,
  // so RoomEvent.Name alone is sufficient.
  useEffect(() => {
    if (!client || !initialSyncComplete) return;

    const handleRoomNameChange = (room) => {
      if (!matrixService.isDMRoom(room.roomId)) return;
      const membership = room.getMyMembership();
      if (membership !== 'join' && membership !== 'invite') return;
      debouncedUpdateRooms();
    };

    client.on(RoomEvent.Name, handleRoomNameChange);

    return () => {
      client.off(RoomEvent.Name, handleRoomNameChange);
    };
  }, [client, initialSyncComplete, debouncedUpdateRooms]);

  // Fetch avatars for all rooms
  const { getRoomAvatar, getFallbackAvatars } = useAvatar();

  useEffect(() => {
    if (!client || !rooms.length) {
      return;
    }

    // Use AbortController to cancel in-flight requests on unmount
    const abortController = new AbortController();
    let cancelled = false;

    const fetchAvatars = async () => {
      const newAvatars = {};

      // Fetch avatars for all rooms in parallel
      await Promise.all(
        rooms.map(async (room) => {
          if (cancelled || abortController.signal.aborted) return;

          try {
            const roomName = room.name || 'Chat';

            // Try explicit room avatar first
            const explicitAvatar = await getRoomAvatar(room.roomId, roomName, 40);

            if (cancelled || abortController.signal.aborted) return;

            if (explicitAvatar) {
              // Room has explicit avatar
              newAvatars[room.roomId] = [{ url: explicitAvatar, type: 'room' }];
            } else {
              // No explicit avatar - get fallback member avatars
              const fallbackAvatars = await getFallbackAvatars(room.roomId, 96);

              if (cancelled || abortController.signal.aborted) return;

              newAvatars[room.roomId] = fallbackAvatars.map(desc => ({
                url: desc.url,
                type: 'user',
              }));
            }
          } catch (error) {
            // Don't log errors if request was aborted
            if (!abortController.signal.aborted) {
              logger.error(`Failed to fetch avatars for room ${room.roomId}:`, error);
              newAvatars[room.roomId] = [];
            }
          }
        }),
      );

      if (!cancelled && !abortController.signal.aborted) {
        // Merge carefully so stale fallback results cannot overwrite explicit avatars
        setRoomAvatars(prev => mergeAvatarDescriptors(prev, newAvatars));
      }
    };

    fetchAvatars();

    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [client, roomIds, getRoomAvatar, getFallbackAvatars]);
  // Note: 'rooms' is intentionally omitted. We only want to re-run when room IDs change
  // (additions/removals), not when room objects are updated. The roomIds memo tracks this.

  // Listen for avatar changes via RoomStateEvent.Members
  // This event always fires when avatars change (via m.room.member state events)
  // UserEvent.AvatarUrl is unreliable and not needed - removed to prevent duplicate work
  const { invalidateAvatar } = useAvatar();

  // Listen for RoomEvent.Timeline to catch avatar changes in m.room.member events
  // Timeline events are delivered to ALL clients via sync, unlike RoomStateEvent.Members
  const handleTimelineEvent = useCallback(async (event, room, toStartOfTimeline) => {
    // Ignore pagination events (events added at start of timeline during backpagination)
    if (toStartOfTimeline) return;

    // Only process m.room.member events
    if (event.getType() !== 'm.room.member') return;

    const content = event.getContent();
    const prevContent = event.getPrevContent();

    // Check if avatar_url changed
    if (content.avatar_url !== prevContent?.avatar_url) {
      const changedUserId = event.getStateKey();
      const myUserId = client.getUserId();

      // Skip self-user avatar changes - AvatarSync handles those
      // This prevents invalidation storms where the same avatar cache is
      // invalidated multiple times (once per room the user is in)
      if (myUserId && changedUserId === myUserId) {
        return;
      }

      // FIX: Skip invalidation during initial sync
      // During initial sync, Matrix replays historical member events (including avatar changes).
      // We don't need to invalidate anything because:
      // 1. Nothing is cached yet - room list will fetch current avatar state directly
      // 2. Historical intermediate states don't matter - only final state matters
      // 3. This prevents race condition where fresh avatars are invalidated immediately after caching
      // After initial sync completes, we process LIVE avatar changes normally.
      if (!initialSyncComplete) {
        return;
      }

      // Find rooms containing this user (including invited)
      // Use client.getRooms() instead of rooms variable to avoid dependency
      const currentRooms = client.getRooms() || [];
      const affectedRooms = currentRooms.filter(room =>
        room.getMembers().some(m =>
          (m.membership === 'join' || m.membership === 'invite') && m.userId === changedUserId,
        ),
      );

      if (affectedRooms.length === 0) {
        return;
      }

      // Invalidate cache via context (revokes blob URLs immediately)
      await invalidateAvatar(changedUserId);

      // FIX: Removed immediate clearing to eliminate flicker
      // Old avatars will remain visible while new ones are fetched
      // This provides a smooth transition instead of showing initials placeholder

      // Re-fetch avatars for affected rooms (async - takes 100-500ms)
      const updates = {};

      await Promise.all(
        affectedRooms.map(async (room) => {
          try {
            const roomName = room.name || 'Chat';

            // Try explicit room avatar first
            const explicitAvatar = await getRoomAvatar(room.roomId, roomName, 40);

            if (explicitAvatar) {
              updates[room.roomId] = [{ url: explicitAvatar, type: 'room' }];
            } else {
              // Get fallback member avatars
              const fallbackAvatars = await getFallbackAvatars(room.roomId, 96);
              updates[room.roomId] = fallbackAvatars.map(desc => ({
                url: desc.url,
                type: 'user',
              }));
            }
          } catch (error) {
            logger.error(`Failed to fetch avatars for room ${room.roomId}:`, error);
            updates[room.roomId] = [];
          }
        }),
      );

      // Update state to trigger re-render
      setRoomAvatars(prev => ({ ...prev, ...updates }));
    }
  }, [client, invalidateAvatar, getRoomAvatar, getFallbackAvatars, initialSyncComplete]);

  useEffect(() => {
    if (!client) {
      return;
    }

    // Listen to RoomEvent.Timeline on the client
    // This captures m.room.member events delivered via sync to ALL clients
    client.on(RoomEvent.Timeline, handleTimelineEvent);

    return () => {
      client.off(RoomEvent.Timeline, handleTimelineEvent);
    };
  }, [client, handleTimelineEvent]);

  // Listen for custom avatar change events (immediate updates on own upload)
  // This handles self-user avatar updates via a custom window event
  // Use useCallback to create a stable event handler reference to avoid re-registering listener
  const handleCustomAvatarChange = useCallback(async (event) => {
    const { userId: changedUserId } = event.detail || {};

    if (!changedUserId) {
      return;
    }

    // Skip during initial sync (same reason as handleMemberStateChange)
    if (!initialSyncComplete) {
      return;
    }

    // Find rooms containing this user (including invited)
    // Use client.getRooms() instead of rooms variable to avoid dependency
    const currentRooms = client?.getRooms() || [];
    const affectedRooms = currentRooms.filter(room =>
      room.getMembers().some(m =>
        (m.membership === 'join' || m.membership === 'invite') && m.userId === changedUserId,
      ),
    );

    if (affectedRooms.length === 0) {
      return;
    }

    // Invalidate cache via context
    await invalidateAvatar(changedUserId);

    // Re-fetch avatars for affected rooms
    const updates = {};

    await Promise.all(
      affectedRooms.map(async (room) => {
        try {
          const roomName = room.name || 'Chat';

          // Try explicit room avatar first
          const explicitAvatar = await getRoomAvatar(room.roomId, roomName, 40);

          if (explicitAvatar) {
            updates[room.roomId] = [{ url: explicitAvatar, type: 'room' }];
          } else {
            // Get fallback member avatars
            const fallbackAvatars = await getFallbackAvatars(room.roomId, 96);
            updates[room.roomId] = fallbackAvatars.map(desc => ({
              url: desc.url,
              type: 'user',
            }));
          }
        } catch (error) {
          logger.error(`Failed to fetch avatars for room ${room.roomId}:`, error);
          updates[room.roomId] = [];
        }
      }),
    );

    // Update state to trigger re-render
    setRoomAvatars(prev => ({ ...prev, ...updates }));
  }, [client, invalidateAvatar, getRoomAvatar, getFallbackAvatars, initialSyncComplete]);

  // Register/unregister the custom avatar change listener once
  useEffect(() => {
    if (!client) {
      return;
    }

    window.addEventListener('matrix:avatar:changed', handleCustomAvatarChange);

    return () => {
      window.removeEventListener('matrix:avatar:changed', handleCustomAvatarChange);
    };
  }, [client, handleCustomAvatarChange]);

  // Listen for room avatar changes and update in real-time
  useEffect(() => {
    if (!client) {
      return;
    }

    const handleRoomAvatarChange = async (event) => {
      // Filter for room avatar events only
      if (event.getType() !== EventType.RoomAvatar) return;

      const changedRoomId = event.getRoomId();

      // Check if this is one of our tracked rooms using live client.getRooms() to avoid stale closure
      // This fixes race condition where avatar is set before effect re-runs with new rooms array
      const allRooms = client.getRooms();
      const dmRooms = allRooms.filter(room => {
        const membership = room.getMyMembership();
        return (membership === 'join' || membership === 'invite') && matrixService.isDMRoom(room.roomId);
      });
      const isTrackedRoom = dmRooms.some(room => room.roomId === changedRoomId);

      if (!isTrackedRoom) {
        return; // Not a room we're tracking
      }

      try {
        const room = client.getRoom(changedRoomId);
        if (!room) {
          logger.warn(`Room ${changedRoomId} not found`);
          return;
        }

        // CRITICAL FIX: Get MXC URL from event content instead of room state
        // The event contains the NEW avatar URL, while room.getMxcAvatarUrl()
        // may return stale data because SDK hasn't updated room state yet
        const eventContent = event.getContent();
        const newAvatarMxc = eventContent?.url || null;

        // Invalidate cache via context (clears old avatar from avatarManager)
        await invalidateAvatar(changedRoomId);

        let newAvatars;

        if (newAvatarMxc) {
          // Avatar was set - use avatarManager to fetch via context
          // avatarManager handles blob URL creation, caching, and lifecycle
          try {
            const room = client.getRoom(changedRoomId);
            const roomName = room?.name || 'Chat';

            const blobUrl = await getRoomAvatar(changedRoomId, roomName, 40);

            if (blobUrl) {
              newAvatars = [{ url: blobUrl, type: 'room' }];
            } else {
              // getRoomAvatar returned null - fallback to member avatars
              const fallbackAvatars = await getFallbackAvatars(changedRoomId, 96);
              newAvatars = fallbackAvatars.map(desc => ({
                url: desc.url,
                type: 'user',
              }));
            }
          } catch (fetchError) {
            logger.warn(`Failed to fetch room avatar for ${changedRoomId}:`, fetchError);
            // Fall back to member avatars
            const fallbackAvatars = await getFallbackAvatars(changedRoomId, 96);
            newAvatars = fallbackAvatars.map(desc => ({
              url: desc.url,
              type: 'user',
            }));
          }
        } else {
          // Avatar was removed (url is null/empty) - use fallback member avatars
          const fallbackAvatars = await getFallbackAvatars(changedRoomId, 96);
          newAvatars = fallbackAvatars.map(desc => ({
            url: desc.url,
            type: 'user',
          }));
        }

        // Update state with new avatars
        setRoomAvatars(prev => ({
          ...prev,
          [changedRoomId]: newAvatars,
        }));
      } catch (error) {
        logger.error('Error updating room avatar:', error);
        // On error, clear the avatar from state to fall back to initials
        setRoomAvatars(prev => {
          const newAvatars = { ...prev };
          delete newAvatars[changedRoomId];
          return newAvatars;
        });
      }
    };

    client.on(RoomStateEvent.Events, handleRoomAvatarChange);

    return () => {
      client.off(RoomStateEvent.Events, handleRoomAvatarChange);
    };
  }, [client, invalidateAvatar, getRoomAvatar, getFallbackAvatars]);

  // Listen for server notices room avatar refresh events
  // This handles the case where a server notices room transitions from invite→join
  useEffect(() => {
    if (!client) {
      return;
    }

    const handleServerNoticesAvatarRefresh = async (event) => {
      const { roomId } = event.detail || {};
      if (!roomId) return;

      // Check if this is one of our DM rooms
      const isDM = rooms.some(room => room.roomId === roomId);
      if (!isDM) return;

      logger.info(`[useRoomList] Server notices avatar refresh triggered for ${roomId}`);

      try {
        const room = client.getRoom(roomId);
        if (!room) return;

        const roomName = getRoomDisplayName(room);
        
        // Force refresh the avatar (server notices should return the special SVG)
        const avatar = await getRoomAvatar(roomId, roomName, 40);
        
        if (avatar) {
          setRoomAvatars(prev => ({
            ...prev,
            [roomId]: [{ url: avatar, type: 'room' }],
          }));
          logger.info(`[useRoomList] Server notices avatar refreshed: ${roomId}`);
        }
      } catch (error) {
        logger.error('[useRoomList] Failed to refresh server notices avatar:', error);
      }
    };

    window.addEventListener('matrix:room:avatar:refresh', handleServerNoticesAvatarRefresh);

    return () => {
      window.removeEventListener('matrix:room:avatar:refresh', handleServerNoticesAvatarRefresh);
    };
  }, [client, rooms, getRoomAvatar]);

  // No cleanup needed - AvatarContext handles all avatar lifecycle management automatically

  const roomList = useMemo(() => {
    if (isLoading || !rooms.length) {
      return [];
    }

    const myUserId = client?.getUserId();

    // Helper function to get room display name considering Gematik custom name
    const getComputedRoomDisplayName = (room) => {
      // Use Gematik utility for room display name if available
      const gematikDisplayName = getRoomDisplayName(room);
      if (gematikDisplayName) {
        return gematikDisplayName;
      }
      
      // Fallback to standard Matrix room name computation
      let roomName = room.name || '';

      // If room.name is empty, try other methods
      if (!roomName) {
        // Try canonical alias
        roomName = room.getCanonicalAlias() || '';
      }

      if (!roomName && room.currentState) {
        // Try to get name from room state (with defensive check)
        const nameEvent = room.currentState.getStateEvents('m.room.name', '');
        if (nameEvent) {
          roomName = nameEvent.getContent().name || '';
        }
      }

      if (!roomName) {
        // For DMs, use m.direct to get the OTHER user (stable during transitions)
        if (matrixService.isDMRoom(room.roomId)) {
          const dmUserId = matrixService.getDMUserForRoom(room.roomId);
          if (dmUserId && dmUserId !== myUserId) {
            const member = room.getMember(dmUserId);
            if (member) {
              roomName = member.name || dmUserId;
            } else {
              // Member not loaded yet, use user ID
              roomName = dmUserId;
            }
          }
        }
      }

      if (!roomName) {
        // Generate a name from members if it's NOT a DM (e.g., group chat)
        const members = room.getJoinedMembers().filter(m => m.userId !== myUserId);
        if (members.length === 1) {
          roomName = members[0].name || members[0].userId;
        } else if (members.length > 1) {
          roomName = members.map(m => m.name || m.userId.split(':')[0].substring(1)).join(', ');
        }
      }

      // Final fallback
      if (!roomName) {
        roomName = room.roomId;
      }

      return roomName;
    };



    const processedList = rooms.map(room => {
      // Get room name - Matrix SDK uses room.name property which computes the display name
      // Try multiple methods to get the room name
      let roomName = getComputedRoomDisplayName(room);

      // For DMs, apply disambiguation if the other user has a duplicate display name
      roomName = getDMDisambiguatedName(room.roomId, room, roomName, myUserId, needsDisambiguation);

      const lastMessage = getLastEvent(room, myUserId, t, ignoredUsers);
      const membership = room.getMyMembership();

      // Extract URLs from descriptors, filtering out any undefined/null entries
      const descriptors = roomAvatars[room.roomId];
      let avatarUrls = [];

      if (Array.isArray(descriptors)) {
        avatarUrls = descriptors
          .filter(descriptor => {
            // Ensure descriptor is an object with a url property
            if (!descriptor || typeof descriptor !== 'object') {
              return false;
            }
            return descriptor.url && typeof descriptor.url === 'string';
          })
          .map(descriptor => descriptor.url);
      }

      // Get mute state from push rules (must be before unreadCount for conditional counting)
      const muteState = getRoomPushRuleState(client, room.roomId);
      const isMuted = muteState !== PushRuleState.NOTIFY;

      // For muted rooms, SDK notification count is always 0 (server doesn't count when
      // push rules suppress). resolveUnreadCount handles the fallback to client-side
      // counting with a module-level cache to survive component remounts. (NED-3296)
      const unreadCount = resolveUnreadCount(room, myUserId, isMuted);

      // Check if the room is encrypted by looking for m.room.encryption state event
      const encryptionEvent = room.currentState.getStateEvents('m.room.encryption', '');
      const isEncrypted = !!encryptionEvent;

      // Check if the room is public by looking for m.room.join_rules state event
      const joinRulesEvent = room.currentState.getStateEvents('m.room.join_rules', '');
      const isPublic = joinRulesEvent?.getContent()?.join_rule === 'public';

      const lastActivity = getLastActivity(room, myUserId);

      // Server notices rooms should never show as invites (auto-joined via useServerNoticesAutoJoin)
      const isServerNotices = isServerNoticesRoom(room, client);

      return {
        id: room.roomId,
        title: roomName,
        name: roomName, // Add name property as well for compatibility
        lastMessage,
        lastActivity,
        avatars: avatarUrls,
        unreadCount,
        isInvite: membership === 'invite' && !isServerNotices,
        topic: getRoomTopic(room),
        isEncrypted,
        isPublic,
        muteState, // 'notify' | 'mentionsOnly' | 'dontNotify'
        isMuted,
      };
    }).filter(Boolean);

    return processedList;
  }, [client, rooms, isLoading, t, roomAvatars, updateTrigger, pushRulesVersion, ignoredUsers, needsDisambiguation]);

  return {
    roomList,
    isLoading,
    addRoom,
  };
};

export default useRoomList;
